import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import { jobToForm, newJobForm, stepForField, toPayload, validateAll, validateStep } from "../../utils/jobOrderForm";
import StepCustomer from "./StepCustomer";
import StepServices from "./StepServices";
import StepPricing from "./StepPricing";
import StepApproval from "./StepApproval";

const STEPS = [
  { title: "Customer Details", sub: "Contact & Address" },
  { title: "Service & Specs", sub: "Items & Description" },
  { title: "Pricing & Payment", sub: "Amounts & Advance" },
  { title: "Approval & Status", sub: "Signatures & Dispatch" },
];

/*
 * The reference's 4-step create/edit wizard. All four panels stay mounted
 * (only the active one is shown) so signature drawings survive step changes.
 */
export default function JobOrderWizard({ job, onClose, onSaved }) {
  const { user, config } = useAuth();
  const showToast = useToast();
  const [form, setForm] = useState(() =>
    job ? jobToForm(job) : newJobForm({ currency: config.default_currency, preparedBy: user.name })
  );
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (job) return;
    apiClient
      .get("job-orders/next-number")
      .then((res) => setForm((current) => (current.job_no ? current : { ...current, job_no: res.data.job_no })))
      .catch(() => {});
  }, [job]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  const update = useCallback((patch) => setForm((current) => ({ ...current, ...patch })), []);

  const fail = ({ field, message, step: errorStep }) => {
    if (errorStep) setStep(errorStep);
    showToast(message, "error");
    setTimeout(() => {
      const input = document.getElementById(`input-${field}`);
      if (input) input.focus();
    }, 50);
  };

  const goNext = () => {
    const error = validateStep(step, form);
    if (error) {
      fail(error);
      return;
    }
    setStep(step + 1);
  };

  const save = async () => {
    const error = validateAll(form);
    if (error) {
      fail(error);
      return;
    }

    setSaving(true);
    try {
      const url = job ? `job-orders/update/${job.id}` : "job-orders/add";
      const res = await apiClient.post(url, toPayload(form));
      showToast(res.data.message);
      onSaved(res.data.job_order);
    } catch (err) {
      setSaving(false);
      const field = err.errors && Object.keys(err.errors)[0];
      if (field) {
        fail({ field: field.split(".")[0], message: err.errors[field], step: stepForField(field) });
      } else {
        showToast(err.message, "error");
      }
    }
  };

  return (
    <div className="modal-backdrop no-print">
      <div className="modal-container wizard-modal">
        <div className="wizard-header">
          <div className="wizard-title-group">
            <h2>{job ? "Edit Job Order" : "Create New Job Order"}</h2>
            <span className="job-num-badge">{form.job_no || "—"}</span>
          </div>
          <button type="button" className="btn-icon-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="wizard-steps-nav">
          {STEPS.map((item, index) => {
            const number = index + 1;
            const state = step === number ? "active" : step > number ? "completed" : "";
            return (
              <Fragment key={item.title}>
                {index > 0 && <div className="step-line"></div>}
                <div className={`step-indicator ${state}`}>
                  <div className="step-num">{number}</div>
                  <div className="step-text">
                    <span className="step-title">{item.title}</span>
                    <span className="step-sub">{item.sub}</span>
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>

        <form className="wizard-body" ref={bodyRef} onSubmit={(e) => e.preventDefault()} noValidate>
          <StepCustomer form={form} update={update} active={step === 1} />
          <StepServices form={form} update={update} services={config.services} active={step === 2} />
          <StepPricing
            form={form}
            update={update}
            currencies={config.currencies}
            paymentMethods={config.payment_methods}
            active={step === 3}
          />
          <StepApproval form={form} update={update} recipientTypes={config.recipient_types} active={step === 4} />
        </form>

        <div className="wizard-footer">
          <button type="button" className="btn btn-secondary" disabled={step === 1} onClick={() => setStep(step - 1)}>
            Back
          </button>
          <div className="footer-right">
            {step < STEPS.length ? (
              <button type="button" className="btn btn-primary" onClick={goNext}>
                Next Step &rarr;
              </button>
            ) : (
              <button type="button" className="btn btn-success" onClick={save} disabled={saving}>
                {saving ? "Saving..." : "💾 Save Job Order"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
